import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Banknote, TrendingUp, TrendingDown, AlertCircle, CheckCircle2,
  ChevronDown, ChevronRight, Pencil, Check, X, Info, ArrowDown, Wallet,
  FileText, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => {
  if (v == null || isNaN(v)) return '-';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
};

const SECTION_COLORS = {
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', header: 'bg-indigo-600 text-white', text: 'text-indigo-700', total: 'bg-indigo-100' },
  slate: { bg: 'bg-slate-50', border: 'border-slate-200', header: 'bg-slate-600 text-white', text: 'text-slate-700', total: 'bg-slate-100' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', header: 'bg-amber-600 text-white', text: 'text-amber-700', total: 'bg-amber-100' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', header: 'bg-emerald-600 text-white', text: 'text-emerald-700', total: 'bg-emerald-100' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-600 text-white', text: 'text-blue-700', total: 'bg-blue-100' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', header: 'bg-purple-600 text-white', text: 'text-purple-700', total: 'bg-purple-100' },
};

const EDITABLE_KEYS = new Set([
  'ricavi', 'costi_variabili', 'costi_fissi',
  'ammortamenti', 'oneri_finanziari', 'imposte', 'compenso_amm',
  'delta_crediti_clienti', 'delta_debiti_fornitori', 'delta_magazzino',
  'delta_debiti_tributari', 'delta_crediti_tributari', 'delta_altri',
  'acquisti_immobilizzazioni', 'vendite_immobilizzazioni',
  'nuovi_prestiti', 'rimborso_prestiti', 'variazione_capitale',
]);

// All explainable keys (everything that has a meaningful breakdown)
const EXPLAINABLE_KEYS = new Set([
  ...Array.from(EDITABLE_KEYS),
  'delta_tfr', 'delta_fondi_rischi',
  'ebitda', 'risultato_netto', 'effetto_ccn', 'aggiustamenti_non_cash',
  'cassa_operativa', 'totale_investimenti', 'totale_finanziamenti',
  'variazione_cassa_pre', 'variazione_cassa',
]);

const RowExplainBtn = ({ clientId, year, fieldKey, label, monthsAvailable, getVal }) => {
  const [open, setOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthsWithValue = useMemo(
    () => (monthsAvailable || []).filter(m => Math.abs(getVal?.(m, fieldKey) || 0) >= 0.01),
    [monthsAvailable, fieldKey, getVal]
  );

  const fetchExplain = async (m) => {
    setLoading(true);
    setData(null);
    try {
      const res = await axios.get(
        `${API}/api/clients/${clientId}/rendiconto/explain`,
        { params: { year, month: m, field_key: fieldKey } }
      );
      setData(res.data);
    } catch (e) {
      setData({ error: 'Errore caricamento spiegazione' });
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (next && !selectedMonth) {
      // Auto-pick the month with the biggest absolute value, fallback to latest
      const candidate = (monthsWithValue.length > 0
        ? monthsWithValue.reduce((a, b) => Math.abs(getVal(b, fieldKey)) > Math.abs(getVal(a, fieldKey)) ? b : a)
        : (monthsAvailable && monthsAvailable[monthsAvailable.length - 1]));
      if (candidate) {
        setSelectedMonth(candidate.month);
        fetchExplain(candidate.month);
      }
    }
  };

  const switchMonth = (m) => {
    setSelectedMonth(m.month);
    fetchExplain(m.month);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full text-slate-300 hover:text-blue-600 hover:bg-blue-50 align-middle"
          onClick={(e) => e.stopPropagation()}
          data-testid={`rendiconto-explain-${fieldKey}`}
          title="Da dove viene questo numero?"
        >
          <Info className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[26rem] p-3 text-xs" side="right" onClick={(e) => e.stopPropagation()}>
        <div className="font-bold text-slate-800 mb-1">{label}</div>
        {/* Month picker */}
        {monthsAvailable && monthsAvailable.length > 1 && (
          <div className="flex flex-wrap gap-0.5 mb-2 border-b pb-2">
            {monthsAvailable.map(m => {
              const v = getVal?.(m, fieldKey) || 0;
              const isActive = selectedMonth === m.month;
              const hasValue = Math.abs(v) >= 0.01;
              return (
                <button
                  key={m.month}
                  onClick={() => switchMonth(m)}
                  className={`text-[10px] px-1.5 py-0.5 rounded font-medium transition-all ${
                    isActive ? 'bg-blue-600 text-white'
                    : hasValue ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                  }`}
                >
                  {m.label || m.month}
                </button>
              );
            })}
          </div>
        )}
        {loading && <div className="text-slate-500">Caricamento...</div>}
        {data?.error && <div className="text-red-600">{data.error}</div>}
        {data && !data.error && (
          <>
            {data.description && (
              <div className="text-[11px] text-slate-600 italic mb-2 leading-relaxed">{data.description}</div>
            )}
            {data.value != null && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-2 flex items-center justify-between">
                <span className="text-slate-600">Valore calcolato:</span>
                <span className="font-bold tabular-nums text-blue-800">{fmt(data.value)}</span>
              </div>
            )}
            {data.manual_override != null && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-[11px] text-amber-800">
                ✏️ <strong>Override manuale</strong>: {fmt(data.manual_override)} — il valore mostrato è quello inserito a mano.
              </div>
            )}
            {data.source === 'ce' && data.items && data.items.length > 0 && (
              <div>
                <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Conti del CE che contribuiscono ({data.item_count})</div>
                <div className="space-y-0.5 max-h-72 overflow-y-auto">
                  {data.items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-1.5 py-1 hover:bg-slate-50 rounded">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] text-slate-400 font-mono">{it.code}</div>
                        <div className="text-[11px] text-slate-700 truncate">{it.name}</div>
                      </div>
                      <div className="font-semibold tabular-nums text-slate-800">{fmt(it.amount)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.source === 'sp' && data.items && (
              <div>
                <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Conti SP (delta mese vs precedente)</div>
                <div className="flex items-center justify-between text-[11px] bg-slate-50 rounded px-2 py-1 mb-1">
                  <span>Tot corrente: <strong>{fmt(data.total_corrente)}</strong></span>
                  <span>Tot precedente: <strong>{fmt(data.totale_precedente)}</strong></span>
                </div>
                {data.items.length === 0 && (
                  <div className="text-[11px] text-slate-500 italic">Nessuna variazione: i saldi non sono cambiati.</div>
                )}
                <div className="space-y-0.5 max-h-72 overflow-y-auto">
                  {data.items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-1.5 py-1 border-b border-slate-100">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] text-slate-400 font-mono">{it.code}</div>
                        <div className="text-[11px] text-slate-700 truncate">{it.name}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-[11px] font-bold tabular-nums ${it.delta < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(it.delta)}</div>
                        <div className="text-[9px] text-slate-400 tabular-nums">{fmt(it.precedente)} → {fmt(it.corrente)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.sub_components && (
              <div className="space-y-1">
                {data.sub_components.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px] bg-slate-50 rounded px-2 py-1">
                    <span className="text-slate-700">{s.label || s.key}{s.sign != null && s.sign < 0 ? ' (sottratto)' : ''}</span>
                    {s.delta != null && <span className="font-semibold tabular-nums">{fmt(s.delta)}</span>}
                  </div>
                ))}
              </div>
            )}
            {data.source === 'manual' && data.manual_override == null && (
              <div className="text-[11px] text-slate-500 italic bg-slate-50 rounded p-2">
                Nessun valore inserito. Per registrarne uno, clicca la matita ✏️ sulla cella e digita l'importo.
              </div>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// VISTA PROFESSIONALE — Formato OIC (metodo indiretto)
// ═══════════════════════════════════════════════════════════════════════

const PROFESSIONAL_ROWS = [
  // Section A: Gestione Reddituale
  { type: 'section_header', label: 'A) FLUSSI FINANZIARI DERIVANTI DALLA GESTIONE REDDITUALE' },
  { type: 'row', key: 'risultato_netto', label: 'Utile (perdita) dell\'esercizio', bold: true },
  { type: 'sub_header', label: 'Rettifiche per elementi non monetari che non hanno avuto contropartita nel CCN:' },
  { type: 'row', key: 'ammortamenti', label: '   Ammortamenti delle immobilizzazioni' },
  { type: 'row', key: 'delta_tfr', label: '   Accantonamento al fondo TFR' },
  { type: 'row', key: 'delta_fondi_rischi', label: '   Accantonamento ai fondi rischi e oneri' },
  { type: 'subtotal', keys: ['risultato_netto', 'ammortamenti', 'delta_tfr', 'delta_fondi_rischi'], label: 'Flusso finanziario prima delle variazioni del CCN' },
  { type: 'sub_header', label: 'Variazioni del Capitale Circolante Netto:' },
  { type: 'row', key: 'delta_crediti_clienti', label: '   Variazione dei crediti verso clienti' },
  { type: 'row', key: 'delta_magazzino', label: '   Variazione delle rimanenze' },
  { type: 'row', key: 'delta_debiti_fornitori', label: '   Variazione dei debiti verso fornitori' },
  { type: 'row', key: 'delta_debiti_tributari', label: '   Variazione debiti tributari e previdenziali' },
  { type: 'row', key: 'delta_crediti_tributari', label: '   Variazione dei crediti tributari' },
  { type: 'row', key: 'delta_altri', label: '   Altre variazioni del capitale circolante' },
  { type: 'total', key: 'cassa_operativa', label: 'A) FLUSSO FINANZIARIO DELLA GESTIONE REDDITUALE', section: 'A' },

  // Section B: Investimenti
  { type: 'section_header', label: 'B) FLUSSI FINANZIARI DERIVANTI DALL\'ATTIVITA\' DI INVESTIMENTO' },
  { type: 'row', key: 'acquisti_immobilizzazioni', label: '   Investimenti in immobilizzazioni' },
  { type: 'row', key: 'vendite_immobilizzazioni', label: '   Disinvestimenti di immobilizzazioni' },
  { type: 'total', key: 'totale_investimenti', label: 'B) FLUSSO FINANZIARIO DELL\'ATTIVITA\' DI INVESTIMENTO', section: 'B' },

  // Section C: Finanziamenti
  { type: 'section_header', label: 'C) FLUSSI FINANZIARI DERIVANTI DALL\'ATTIVITA\' DI FINANZIAMENTO' },
  { type: 'row', key: 'nuovi_prestiti', label: '   Accensione finanziamenti' },
  { type: 'row', key: 'rimborso_prestiti', label: '   Rimborso finanziamenti' },
  { type: 'row', key: 'variazione_capitale', label: '   Variazione di capitale e riserve' },
  { type: 'total', key: 'totale_finanziamenti', label: 'C) FLUSSO FINANZIARIO DELL\'ATTIVITA\' DI FINANZIAMENTO', section: 'C' },

  // Summary
  { type: 'subtotal', keys: ['cassa_operativa', 'totale_investimenti', 'totale_finanziamenti'], label: 'Variazione calcolata (A+B+C)' },
  { type: 'row', key: 'rettifica_ce_sp', label: '   Rettifica di quadratura CE/SP', note: 'Differenza tra risultato CE e risultato implicito SP' },
  { type: 'grand_total', key: 'variazione_cassa', label: 'INCREMENTO (DECREMENTO) DELLE DISPONIBILITA\' LIQUIDE' },
  { type: 'separator' },
  { type: 'info_row', key: 'liquidita_inizio', label: 'Disponibilita\' liquide a inizio esercizio', onlyFirst: true },
  { type: 'info_row', key: 'liquidita_sp', label: 'Disponibilita\' liquide a fine esercizio (da SP)', useLast: true },
];

const ProfessionalView = ({ months, summary, data, fmt }) => {
  const getVal = (m, key) => m?.values?.[key];
  const getSummaryVal = (key) => summary?.[key];
  const fmtCell = (v) => v != null && v !== 0 ? fmt(v) : '-';

  // For subtotal rows, compute inline
  const computeSubtotal = (m, keys) => {
    let total = 0;
    for (const k of keys) {
      total += (m?.values?.[k] ?? 0);
    }
    return total;
  };
  const computeSubtotalSummary = (keys) => {
    let total = 0;
    for (const k of keys) {
      total += (summary?.[k] ?? 0);
    }
    return total;
  };

  return (
    <Card className="border-slate-300" data-testid="rendiconto-professionale">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="professional-table">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="text-left py-2.5 px-4 font-bold text-sm min-w-[350px] tracking-wide sticky left-0 z-20 bg-slate-800 border-r border-slate-600">RENDICONTO FINANZIARIO</th>
                {months.map(m => (
                  <th key={m.month} className="text-right py-2.5 px-3 font-semibold min-w-[95px]">
                    {m.month_name}
                  </th>
                ))}
                {months.length > 1 && (
                  <th className="text-right py-2.5 px-3 font-bold min-w-[110px] bg-slate-900">TOTALE</th>
                )}
              </tr>
            </thead>
            <tbody>
              {PROFESSIONAL_ROWS.map((row, i) => {
                if (row.type === 'section_header') {
                  return (
                    <tr key={i} className="bg-slate-100 border-t-2 border-slate-300">
                      <td className="py-2.5 px-4 font-bold text-slate-800 text-xs tracking-wide sticky left-0 z-10 bg-slate-100 border-r border-slate-200" colSpan={months.length + 2}>
                        {row.label}
                      </td>
                    </tr>
                  );
                }
                if (row.type === 'sub_header') {
                  return (
                    <tr key={i} className="bg-slate-50/70">
                      <td className="py-1.5 px-4 text-slate-500 italic text-[11px] sticky left-0 z-10 bg-slate-50 border-r border-slate-200" colSpan={months.length + 2}>
                        {row.label}
                      </td>
                    </tr>
                  );
                }
                if (row.type === 'separator') {
                  return <tr key={i}><td className="py-1 border-t-2 border-slate-400" colSpan={months.length + 2}></td></tr>;
                }
                if (row.type === 'row') {
                  return (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className={`py-1.5 px-4 text-slate-700 ${row.bold ? 'font-semibold' : ''} sticky left-0 z-10 bg-white border-r border-slate-200`}>
                        {row.label}
                        {EXPLAINABLE_KEYS.has(row.key) && (
                          <RowExplainBtn
                            clientId={clientId}
                            year={year}
                            fieldKey={row.key}
                            label={row.label.trim()}
                            monthsAvailable={months}
                            getVal={getVal}
                          />
                        )}
                      </td>
                      {months.map(m => {
                        const v = getVal(m, row.key);
                        return (
                          <td key={m.month} className={`text-right py-1.5 px-3 tabular-nums ${v < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                            {fmtCell(v)}
                          </td>
                        );
                      })}
                      {months.length > 1 && (
                        <td className={`text-right py-1.5 px-3 tabular-nums bg-slate-50/50 font-medium ${(getSummaryVal(row.key) ?? 0) < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                          {fmtCell(getSummaryVal(row.key))}
                        </td>
                      )}
                    </tr>
                  );
                }
                if (row.type === 'subtotal') {
                  return (
                    <tr key={i} className="border-b border-slate-200 bg-slate-50/50">
                      <td className="py-1.5 px-4 font-medium text-slate-700 italic sticky left-0 z-10 bg-slate-50 border-r border-slate-200">{row.label}</td>
                      {months.map(m => {
                        const v = computeSubtotal(m, row.keys);
                        return (
                          <td key={m.month} className={`text-right py-1.5 px-3 tabular-nums font-medium ${v < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                            {fmtCell(v)}
                          </td>
                        );
                      })}
                      {months.length > 1 && (
                        <td className={`text-right py-1.5 px-3 tabular-nums bg-slate-100 font-semibold ${computeSubtotalSummary(row.keys) < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                          {fmtCell(computeSubtotalSummary(row.keys))}
                        </td>
                      )}
                    </tr>
                  );
                }
                if (row.type === 'total') {
                  return (
                    <tr key={i} className="bg-slate-200/70 border-t-2 border-b-2 border-slate-300">
                      <td className="py-2 px-4 font-bold text-slate-900 text-xs sticky left-0 z-10 bg-slate-200 border-r border-slate-300">
                        {row.label}
                        {EXPLAINABLE_KEYS.has(row.key) && (
                          <RowExplainBtn
                            clientId={clientId}
                            year={year}
                            fieldKey={row.key}
                            label={row.label.trim()}
                            monthsAvailable={months}
                            getVal={getVal}
                          />
                        )}
                      </td>
                      {months.map(m => {
                        const v = getVal(m, row.key);
                        return (
                          <td key={m.month} className={`text-right py-2 px-3 tabular-nums font-bold ${v < 0 ? 'text-red-700' : 'text-slate-900'}`}>
                            {fmtCell(v)}
                          </td>
                        );
                      })}
                      {months.length > 1 && (
                        <td className={`text-right py-2 px-3 tabular-nums font-bold bg-slate-300/50 ${(getSummaryVal(row.key) ?? 0) < 0 ? 'text-red-700' : 'text-slate-900'}`}>
                          {fmtCell(getSummaryVal(row.key))}
                        </td>
                      )}
                    </tr>
                  );
                }
                if (row.type === 'grand_total') {
                  return (
                    <tr key={i} className="bg-slate-800 text-white border-t-2 border-slate-400">
                      <td className="py-3 px-4 font-bold text-sm tracking-wide sticky left-0 z-10 bg-slate-800 border-r border-slate-600">{row.label}</td>
                      {months.map(m => {
                        const v = getVal(m, row.key);
                        return (
                          <td key={m.month} className={`text-right py-3 px-3 tabular-nums font-bold text-sm ${v < 0 ? 'text-red-300' : 'text-white'}`}>
                            {fmtCell(v)}
                          </td>
                        );
                      })}
                      {months.length > 1 && (
                        <td className={`text-right py-3 px-3 tabular-nums font-bold text-sm bg-slate-900 ${(getSummaryVal(row.key) ?? 0) < 0 ? 'text-red-300' : 'text-white'}`}>
                          {fmtCell(getSummaryVal(row.key))}
                        </td>
                      )}
                    </tr>
                  );
                }
                if (row.type === 'info_row') {
                  return (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 px-4 font-semibold text-slate-700 sticky left-0 z-10 bg-white border-r border-slate-200">{row.label}</td>
                      {months.map((m, mi) => {
                        let v = null;
                        if (row.onlyFirst && mi === 0) v = getVal(m, row.key);
                        else if (row.useLast) v = getVal(m, row.key);
                        else if (!row.onlyFirst && !row.useLast) v = getVal(m, row.key);
                        return (
                          <td key={m.month} className={`text-right py-2 px-3 tabular-nums font-medium ${v != null ? 'text-slate-800' : 'text-slate-200'}`}>
                            {v != null ? fmtCell(v) : '-'}
                          </td>
                        );
                      })}
                      {months.length > 1 && (
                        <td className="text-right py-2 px-3 tabular-nums font-semibold bg-slate-50/50 text-slate-800">
                          {row.onlyFirst && data?.initial_balance != null ? fmtCell(data.initial_balance)
                            : row.useLast && months.length > 0 ? fmtCell(months[months.length - 1]?.values?.liquidita_sp)
                            : '-'}
                        </td>
                      )}
                    </tr>
                  );
                }
                return null;
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-[10px] text-slate-400 border-t border-slate-200">
          Rendiconto finanziario redatto con il metodo indiretto ai sensi dell'OIC 10
        </div>
      </CardContent>
    </Card>
  );
};

const RendicontoFinanziario = ({ clientId, periods }) => {
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editingBalance, setEditingBalance] = useState(false);
  const [balanceValue, setBalanceValue] = useState('');
  const [viewMode, setViewMode] = useState('consulenza'); // 'consulenza' | 'professionale'

  const availableYears = useMemo(() => {
    if (!periods?.length) return [];
    return [...new Set(periods.map(p => p.year))].sort((a, b) => b - a);
  }, [periods]);

  useEffect(() => {
    if (availableYears.length && !year) setYear(String(availableYears[0]));
  }, [availableYears, year]);

  const fetchData = useCallback(async () => {
    if (!year || !clientId) return;
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const resp = await fetch(`${API}/api/clients/${clientId}/rendiconto?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) setData(await resp.json());
      else toast.error('Errore nel caricamento');
    } catch { toast.error('Errore di connessione'); }
    setLoading(false);
  }, [year, clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveEdit = async () => {
    if (!editingCell) return;
    const token = localStorage.getItem('token');
    try {
      const amount = editValue.trim() === '' ? null : parseFloat(editValue.replace(',', '.'));
      await fetch(`${API}/api/clients/${clientId}/rendiconto/adjustment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: parseInt(year), month: editingCell.month, field_key: editingCell.key, amount }),
      });
      toast.success('Valore aggiornato');
      setEditingCell(null);
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const saveInitialBalance = async () => {
    const token = localStorage.getItem('token');
    try {
      const amount = balanceValue.trim() === '' ? null : parseFloat(balanceValue.replace(',', '.'));
      await fetch(`${API}/api/clients/${clientId}/rendiconto/initial-balance`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: parseInt(year), amount }),
      });
      toast.success('Saldo iniziale salvato');
      setEditingBalance(false);
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const toggleCollapse = (id) => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  if (loading && !data) return <div className="flex items-center justify-center py-20 text-muted-foreground">Caricamento...</div>;
  if (!data) return null;

  const months = data.months || [];
  const summary = data.summary || {};
  const sections = data.flow_sections || [];
  const hasData = months.length > 0;
  const hasSP = data.has_sp_data;

  // Value cell renderer
  const ValCell = ({ value, isManual, rowKey, month, isTotal }) => {
    const isEditing = editingCell?.month === month && editingCell?.key === rowKey;
    const editable = EDITABLE_KEYS.has(rowKey) && !isTotal;

    if (isEditing) {
      return (
        <td className="py-1 px-1 bg-blue-50 border-r border-slate-100">
          <div className="flex items-center gap-0.5">
            <Input value={editValue} onChange={e => setEditValue(e.target.value)}
              className="h-7 text-xs text-right w-20" autoFocus
              onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingCell(null); }} />
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={saveEdit}><Check className="h-3 w-3 text-green-600" /></Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditingCell(null)}><X className="h-3 w-3 text-red-500" /></Button>
          </div>
        </td>
      );
    }

    const color = value == null || value === 0 ? 'text-slate-300' : value < 0 ? 'text-red-600 font-medium' : 'text-slate-800';
    return (
      <td className={`text-right py-1.5 px-3 border-r border-slate-100 ${editable ? 'cursor-pointer hover:bg-blue-50/50' : ''} ${isManual ? 'bg-amber-50/50' : ''}`}
        onClick={() => editable && (() => { setEditingCell({ month, key: rowKey }); setEditValue(value != null ? String(value) : ''); })()}
        title={editable ? 'Clicca per modificare' : ''}>
        <span className={`${color} text-xs`}>
          {isManual && <Badge variant="outline" className="h-3.5 px-0.5 text-[8px] text-amber-600 border-amber-300 mr-0.5 inline">M</Badge>}
          {value != null && value !== 0 ? fmt(value) : '-'}
        </span>
      </td>
    );
  };

  return (
    <div className="space-y-4" data-testid="rendiconto-finanziario">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Banknote className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Rendiconto Finanziario</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {viewMode === 'professionale'
                    ? 'Metodo indiretto — formato ufficiale OIC'
                    : "Dall'EBITDA alla cassa reale: ogni passaggio spiegato"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Tabs value={viewMode} onValueChange={setViewMode} className="hidden sm:block">
                <TabsList className="h-9" data-testid="rendiconto-view-tabs">
                  <TabsTrigger value="professionale" className="text-xs gap-1.5 px-3" data-testid="tab-professionale">
                    <FileText className="h-3.5 w-3.5" />Professionale
                  </TabsTrigger>
                  <TabsTrigger value="consulenza" className="text-xs gap-1.5 px-3" data-testid="tab-consulenza">
                    <MessageCircle className="h-3.5 w-3.5" />Consulenza
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-28" data-testid="rendiconto-year-select">
                  <SelectValue placeholder="Anno" />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Mobile tab fallback */}
          <div className="sm:hidden mt-3">
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList className="w-full h-9">
                <TabsTrigger value="professionale" className="text-xs flex-1 gap-1">
                  <FileText className="h-3.5 w-3.5" />Professionale
                </TabsTrigger>
                <TabsTrigger value="consulenza" className="text-xs flex-1 gap-1">
                  <MessageCircle className="h-3.5 w-3.5" />Consulenza
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
      </Card>

      {!hasData ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <AlertCircle className="h-12 w-12 text-amber-400 mb-4" />
            <h3 className="text-base font-semibold text-slate-700 mb-2">Nessun dato disponibile</h3>
            <p className="text-sm text-muted-foreground">Carica i bilanci mensili per generare il rendiconto.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Initial Balance Input */}
          <Card className="border-emerald-200 bg-emerald-50/30" data-testid="initial-balance-card">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Saldo in banca a inizio anno</p>
                    <p className="text-xs text-emerald-600">Quanto avevi sul conto a gennaio {year}?</p>
                  </div>
                </div>
                {editingBalance ? (
                  <div className="flex items-center gap-2">
                    <Input value={balanceValue} onChange={e => setBalanceValue(e.target.value)}
                      className="h-8 w-32 text-right text-sm" placeholder="0" autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') saveInitialBalance(); if (e.key === 'Escape') setEditingBalance(false); }} />
                    <Button size="sm" onClick={saveInitialBalance}><Check className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingBalance(false)}><X className="h-4 w-4" /></Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="gap-1.5 border-emerald-300 text-emerald-700"
                    onClick={() => { setEditingBalance(true); setBalanceValue(data.initial_balance != null ? String(data.initial_balance) : ''); }}
                    data-testid="set-initial-balance-btn">
                    {data.initial_balance != null ? (
                      <span className="font-bold text-emerald-800">{fmt(data.initial_balance)}</span>
                    ) : (
                      <span>Imposta saldo iniziale</span>
                    )}
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* SP warning */}
          {!hasSP && (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <p>Dati SP mancanti. Le voci di capitale circolante, investimenti e finanziamenti saranno a zero. Carica i dati dello Stato Patrimoniale.</p>
            </div>
          )}

          {/* ═══════ VISTA PROFESSIONALE (OIC) ═══════ */}
          {viewMode === 'professionale' && (
            <ProfessionalView months={months} summary={summary} data={data} fmt={fmt} />
          )}

          {/* ═══════ VISTA CONSULENZA (attuale) ═══════ */}
          {viewMode === 'consulenza' && (
            <>
          {/* Flow sections */}
          {sections.map((section, sIdx) => {
            const colors = SECTION_COLORS[section.color] || SECTION_COLORS.slate;
            const isOpen = collapsed[section.id] !== true;

            if (section.is_grand_total) {
              // Grand total row - show as a prominent card
              return (
                <Card key={section.id} className={`${colors.border} border-2`} data-testid={`rf-section-${section.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`h-8 w-8 rounded-full ${colors.total} flex items-center justify-center`}>
                        <TrendingUp className={`h-4 w-4 ${colors.text}`} />
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${colors.text}`}>{section.total_label}</p>
                        <p className="text-xs text-muted-foreground">{section.description}</p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr>
                            <th className="min-w-[200px] sticky left-0 z-20 bg-white"></th>
                            {months.map(m => (
                              <th key={m.month} className="text-right text-xs font-medium text-slate-500 px-3 py-1 min-w-[100px]">
                                {m.month_name}
                              </th>
                            ))}
                            {months.length > 1 && <th className="text-right text-xs font-bold text-slate-600 px-3 py-1 min-w-[110px]">Totale</th>}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className={`${colors.total} rounded-lg`}>
                            <td className={`py-2 px-3 font-bold text-sm ${colors.text} sticky left-0 z-10 border-r border-slate-200`} style={{backgroundColor: 'inherit'}}>{section.total_label}</td>
                            {months.map(m => {
                              const val = m.values?.[section.total_key];
                              return (
                                <td key={m.month} className={`text-right py-2 px-3 font-bold text-sm ${val < 0 ? 'text-red-600' : colors.text}`}>
                                  {fmt(val)}
                                </td>
                              );
                            })}
                            {months.length > 1 && (
                              <td className={`text-right py-2 px-3 font-bold text-sm ${summary[section.total_key] < 0 ? 'text-red-600' : colors.text}`}>
                                {fmt(summary[section.total_key])}
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2 italic">{section.formula}</p>
                  </CardContent>
                </Card>
              );
            }

            return (
              <Card key={section.id} className={isOpen ? colors.border : 'border-slate-200'} data-testid={`rf-section-${section.id}`}>
                <div className={`flex items-center justify-between px-4 py-2.5 cursor-pointer rounded-t-lg ${colors.header}`}
                  onClick={() => toggleCollapse(section.id)}>
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <span className="text-sm font-bold">{section.title}</span>
                  </div>
                  {/* Show section total inline when collapsed */}
                  {!isOpen && section.total_key && (
                    <span className="text-sm font-bold opacity-90">{fmt(summary[section.total_key])}</span>
                  )}
                </div>

                {isOpen && (
                  <CardContent className="p-0">
                    <p className="text-xs text-muted-foreground px-4 py-2 border-b border-slate-100">
                      {section.description}
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-slate-50/50">
                            <th className="text-left py-1.5 px-4 font-medium text-slate-500 min-w-[200px] sticky left-0 z-20 bg-slate-50 border-r border-slate-200">Voce</th>
                            {months.map(m => (
                              <th key={m.month} className="text-right py-1.5 px-3 font-medium text-slate-500 min-w-[100px]">
                                {m.month_name}
                              </th>
                            ))}
                            {months.length > 1 && <th className="text-right py-1.5 px-3 font-bold text-slate-600 min-w-[110px] bg-slate-100/50">Totale</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {section.rows?.map(row => (
                            <tr key={row.key} className="border-b border-slate-100/50 hover:bg-slate-50/50">
                              <td className="py-1.5 px-4 text-slate-700 sticky left-0 z-10 bg-white border-r border-slate-200">
                                <span className="flex items-center gap-1">
                                  {row.label}
                                  {EDITABLE_KEYS.has(row.key) && <Pencil className="h-2.5 w-2.5 text-slate-300 flex-shrink-0" />}
                                  {EXPLAINABLE_KEYS.has(row.key) && (
                                    <RowExplainBtn
                                      clientId={clientId}
                                      year={year}
                                      fieldKey={row.key}
                                      label={row.label}
                                      monthsAvailable={months}
                                      getVal={(m, k) => m.values?.[k] ?? 0}
                                    />
                                  )}
                                </span>
                                {row.note && <span className="text-[10px] text-slate-400 block">{row.note}</span>}
                              </td>
                              {months.map(m => (
                                <ValCell key={m.month} value={m.values?.[row.key]}
                                  isManual={m.is_manual?.[row.key]} rowKey={row.key} month={m.month} />
                              ))}
                              {months.length > 1 && (
                                <td className="text-right py-1.5 px-3 bg-slate-50/50 font-medium text-slate-700 text-xs">
                                  {fmt(summary[row.key])}
                                </td>
                              )}
                            </tr>
                          ))}
                          {/* Section total */}
                          {section.total_key && (
                            <tr className={`${colors.total} font-semibold`}>
                              <td className={`py-2 px-4 ${colors.text} text-xs font-bold sticky left-0 z-10 border-r border-slate-200`} style={{backgroundColor: 'inherit'}}>
                                <span className="inline-flex items-center gap-1">
                                  {section.total_label}
                                  {EXPLAINABLE_KEYS.has(section.total_key) && (
                                    <RowExplainBtn
                                      clientId={clientId}
                                      year={year}
                                      fieldKey={section.total_key}
                                      label={section.total_label}
                                      monthsAvailable={months}
                                      getVal={(m, k) => m.values?.[k] ?? 0}
                                    />
                                  )}
                                </span>
                              </td>
                              {months.map(m => {
                                const val = m.values?.[section.total_key];
                                return (
                                  <td key={m.month} className={`text-right py-2 px-3 font-bold text-xs ${val < 0 ? 'text-red-600' : colors.text}`}>
                                    {fmt(val)}
                                  </td>
                                );
                              })}
                              {months.length > 1 && (
                                <td className={`text-right py-2 px-3 font-bold text-xs ${summary[section.total_key] < 0 ? 'text-red-600' : colors.text}`}>
                                  {fmt(summary[section.total_key])}
                                </td>
                              )}
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}

          {/* Arrow connector */}
          <div className="flex justify-center py-1">
            <ArrowDown className="h-6 w-6 text-slate-300" />
          </div>

          {/* VERIFICATION BOX */}
          <Card className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white" data-testid="verifica-finale">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                Verifica finale: quadra la cassa?
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Confronto mese per mese tra la variazione calcolata dal modello e quella reale dallo Stato Patrimoniale
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left py-2 px-3 min-w-[250px] sticky left-0 z-20 bg-white border-r border-slate-200"></th>
                      {months.map(m => (
                        <th key={m.month} className="text-right py-2 px-3 text-xs font-medium text-slate-500 min-w-[100px]">
                          {m.month_name}
                        </th>
                      ))}
                      {months.length > 1 && <th className="text-right py-2 px-3 text-xs font-bold text-slate-600 min-w-[110px]">Totale</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Variazione modello (pre-rettifica) */}
                    <tr className="bg-blue-50/50 border-b border-blue-100">
                      <td className="py-2 px-3 font-medium text-blue-800 text-xs sticky left-0 z-10 bg-blue-50 border-r border-slate-200">
                        Variazione calcolata (dal CE)
                      </td>
                      {months.map(m => (
                        <td key={m.month} className={`text-right py-2 px-3 text-xs font-bold ${(m.values?.variazione_cassa_pre ?? m.values?.variazione_cassa) < 0 ? 'text-red-600' : 'text-blue-800'}`}>
                          {fmt(m.values?.variazione_cassa_pre ?? m.values?.variazione_cassa)}
                        </td>
                      ))}
                      {months.length > 1 && (
                        <td className={`text-right py-2 px-3 text-xs font-bold ${(summary.variazione_cassa_pre ?? summary.variazione_cassa) < 0 ? 'text-red-600' : 'text-blue-800'}`}>
                          {fmt(summary.variazione_cassa_pre ?? summary.variazione_cassa)}
                        </td>
                      )}
                    </tr>
                    {/* Variazione reale da SP */}
                    <tr className="bg-emerald-50/50 border-b border-emerald-100">
                      <td className="py-2 px-3 font-medium text-emerald-800 text-xs sticky left-0 z-10 bg-emerald-50 border-r border-slate-200">
                        Variazione reale (da Stato Patrimoniale)
                      </td>
                      {months.map(m => {
                        const v = m.values?.delta_liquidita_sp;
                        return (
                          <td key={m.month} className={`text-right py-2 px-3 text-xs font-bold ${v == null ? 'text-slate-300' : v < 0 ? 'text-red-600' : 'text-emerald-800'}`}>
                            {v != null ? fmt(v) : 'n.d.'}
                          </td>
                        );
                      })}
                      {months.length > 1 && (
                        <td className={`text-right py-2 px-3 text-xs font-bold ${summary.delta_liquidita_sp < 0 ? 'text-red-600' : 'text-emerald-800'}`}>
                          {summary.delta_liquidita_sp != null ? fmt(summary.delta_liquidita_sp) : 'n.d.'}
                        </td>
                      )}
                    </tr>
                    {/* Rettifica CE/SP */}
                    <tr className="bg-amber-50/50 border-b border-amber-200">
                      <td className="py-2 px-3 font-medium text-amber-800 text-xs sticky left-0 z-10 bg-amber-50 border-r border-slate-200">
                        Rettifica CE/SP
                        <span className="block text-[10px] text-amber-500 font-normal">Aggiusta il modello alla realta SP</span>
                      </td>
                      {months.map(m => {
                        const rett = m.values?.rettifica_ce_sp;
                        return (
                          <td key={m.month} className={`text-right py-2 px-3 text-xs font-bold ${rett == null || rett === 0 ? 'text-slate-400' : 'text-amber-700'}`}>
                            {rett != null && rett !== 0 ? fmt(rett) : (m.values?.delta_liquidita_sp != null ? '-' : 'n.d.')}
                          </td>
                        );
                      })}
                      {months.length > 1 && (
                        <td className={`text-right py-2 px-3 text-xs font-bold text-amber-700`}>
                          {fmt(summary.rettifica_ce_sp)}
                        </td>
                      )}
                    </tr>
                    {/* Differenza dopo rettifica (should be ~0) */}
                    <tr>
                      <td className="py-2.5 px-3 font-bold text-xs sticky left-0 z-10 bg-white border-r border-slate-200">
                        Differenza residua
                        <span className="block text-[10px] text-slate-400 font-normal">Dopo rettifica (deve essere zero)</span>
                      </td>
                      {months.map(m => {
                        const diff = m.values?.differenza_verifica;
                        const ok = diff != null && Math.abs(diff) < 100;
                        return (
                          <td key={m.month} className="text-right py-2.5 px-3">
                            {diff != null ? (
                              <span className={`inline-flex items-center gap-1 text-xs font-bold ${ok ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                                {ok ? 'OK' : fmt(diff)}
                              </span>
                            ) : <span className="text-slate-300 text-xs">-</span>}
                          </td>
                        );
                      })}
                      {months.length > 1 && (() => {
                        const diff = summary.differenza_verifica;
                        const ok = diff != null && Math.abs(diff) < 500;
                        return (
                          <td className="text-right py-2.5 px-3">
                            {diff != null ? (
                              <span className={`inline-flex items-center gap-1 text-xs font-bold ${ok ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                                {ok ? 'QUADRA' : fmt(diff)}
                              </span>
                            ) : '-'}
                          </td>
                        );
                      })()}
                    </tr>
                    {/* Saldo SP reale */}
                    <tr className="border-t-2 border-emerald-200 bg-emerald-100/50">
                      <td className="py-2.5 px-3 font-bold text-emerald-900 text-xs sticky left-0 z-10 bg-emerald-100 border-r border-slate-200">
                        Saldo reale in banca (SP)
                      </td>
                      {months.map(m => (
                        <td key={m.month} className="text-right py-2.5 px-3 text-xs font-bold text-emerald-900">
                          {m.values?.liquidita_sp != null ? fmt(m.values.liquidita_sp) : <span className="text-slate-300">n.d.</span>}
                        </td>
                      ))}
                      {months.length > 1 && (
                        <td className="text-right py-2.5 px-3 text-xs font-bold text-emerald-900">
                          {summary.liquidita_sp != null ? fmt(summary.liquidita_sp) : 'n.d.'}
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Visual summary */}
              {(() => {
                const skippedMonths = months.filter(m => m.values?.differenza_verifica == null && m.has_sp_data);
                return skippedMonths.length > 0 ? (
                  <div className="mt-3 flex items-start gap-2 px-2 py-2 rounded bg-slate-50 border border-slate-200 text-[11px] text-slate-500">
                    <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      Il mese di <b>{skippedMonths.map(m => m.month_name).join(', ')}</b> e escluso dalla verifica perche non e disponibile un bilancio precedente per il confronto.
                      Il totale confronta solo i mesi con dati SP comparabili.
                    </span>
                  </div>
                ) : null;
              })()}
              {summary.liquidita_inizio != null && (
                <div className="mt-4 flex items-center gap-2 flex-wrap justify-center text-xs">
                  <div className="px-3 py-1.5 rounded-full bg-slate-100 font-medium text-slate-700">
                    Inizio: {fmt(summary.liquidita_inizio)}
                  </div>
                  <ArrowDown className="h-4 w-4 text-slate-400 rotate-[-90deg]" />
                  <div className="px-3 py-1.5 rounded-full bg-emerald-100 font-medium text-emerald-700">
                    + Gestione: {fmt(summary.cassa_operativa)}
                  </div>
                  <ArrowDown className="h-4 w-4 text-slate-400 rotate-[-90deg]" />
                  <div className="px-3 py-1.5 rounded-full bg-blue-100 font-medium text-blue-700">
                    + Investimenti: {fmt(summary.totale_investimenti)}
                  </div>
                  <ArrowDown className="h-4 w-4 text-slate-400 rotate-[-90deg]" />
                  <div className="px-3 py-1.5 rounded-full bg-purple-100 font-medium text-purple-700">
                    + Finanz.: {fmt(summary.totale_finanziamenti)}
                  </div>
                  <ArrowDown className="h-4 w-4 text-slate-400 rotate-[-90deg]" />
                  <div className={`px-3 py-1.5 rounded-full font-bold ${
                    summary.liquidita_fine_calc >= 0 ? 'bg-emerald-200 text-emerald-800' : 'bg-red-200 text-red-800'
                  }`}>
                    = Fine: {fmt(summary.liquidita_fine_calc)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          <div className="flex items-center gap-4 text-[10px] text-muted-foreground flex-wrap px-1">
            <span className="flex items-center gap-1"><Pencil className="h-2.5 w-2.5" /> Clicca su un valore per modificarlo manualmente</span>
            <span className="flex items-center gap-1"><Badge variant="outline" className="h-3.5 px-0.5 text-[8px] text-amber-600 border-amber-300">M</Badge> Valore manuale</span>
          </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RendicontoFinanziario;
